import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header9 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template9' },
        { label: 'Shop', path: '/template9/shop' },
        { label: 'About', path: '/template9/about' },
        { label: 'Contact', path: '/template9/contact' },
        { label: 'Orders', path: '/template9/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <header
                className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
                style={{
                    backgroundColor: scrolled ? '#FFFFFF' : 'transparent',
                    borderBottom: scrolled ? '1px solid #E2E8F0' : '1px solid transparent',
                    boxShadow: scrolled ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16 md:h-20">
                    {/* Logo */}
                    <div
                        onClick={() => navigate('/template9')}
                        className="cursor-pointer flex items-center gap-2"
                    >
                        <div className="w-2 h-6 bg-[#2563EB] rounded-none" />
                        <span
                            className="text-xl md:text-2xl font-bold tracking-[3px] text-[#0F172A]"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            NAKSHA
                        </span>
                    </div>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                className="text-sm font-medium text-[#0F172A] hover:text-[#2563EB] tracking-wider uppercase transition-colors duration-300 bg-transparent border-none cursor-pointer"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right Icons */}
                    <div className="flex items-center gap-4">
                        <Search
                            size={20}
                            className="text-[#0F172A] cursor-pointer hover:text-[#2563EB] transition-colors duration-300 hidden md:block"
                        />
                        <User
                            size={20}
                            className="text-[#0F172A] cursor-pointer hover:text-[#2563EB] transition-colors duration-300 hidden md:block"
                            onClick={() => navigate('/template9/profile')}
                        />
                        <div
                            className="relative cursor-pointer"
                            onClick={() => navigate('/template9/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                className="text-[#0F172A] hover:text-[#2563EB] transition-colors duration-300"
                            />
                            {cartCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-[#2563EB] text-white text-[10px] font-bold w-[18px] h-[18px] flex items-center justify-center rounded-none">
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="md:hidden bg-transparent border-none cursor-pointer p-0"
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <Menu size={24} className="text-[#0F172A]" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center transition-opacity duration-300"
                style={{
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                }}
            >
                {/* Close Button */}
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="absolute top-4 right-4 bg-transparent border-none cursor-pointer p-0"
                >
                    <X size={28} className="text-[#0F172A]" />
                </button>

                {/* Blue accent line */}
                <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />

                <nav className="flex flex-col items-center gap-8">
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            className="text-2xl font-semibold text-[#0F172A] hover:text-[#2563EB] bg-transparent border-none cursor-pointer tracking-[3px] uppercase transition-colors duration-300"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* Bottom icons */}
                <div className="absolute bottom-12 flex items-center gap-8">
                    <User
                        size={22}
                        className="text-[#0F172A] cursor-pointer hover:text-[#2563EB] transition-colors duration-300"
                        onClick={() => { navigate('/template9/profile'); setMobileMenuOpen(false); }}
                    />
                    <Search
                        size={22}
                        className="text-[#0F172A] cursor-pointer hover:text-[#2563EB] transition-colors duration-300"
                    />
                </div>
            </div>
        </>
    );
};

export default Header9;
